<template>
  <div>
    <!-- Advanced Filters -->
    <AdvancedFilters 
      :data="invoices"
      :user-companies="userCompanies"
      :export-filename="'Submitted_Invoices'"
      :export-columns="exportColumns"
      :on-export="handleExportData"
      search-placeholder="Search submitted invoices..."
      @update:searchQuery="updateSearchQuery"
      @filter-change="handleFilterChange"
    />

    <!-- Table -->
    <div class="overflow-x-auto border border-gray-200 rounded-md">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <!-- Invoice ID -->
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div class="flex items-center cursor-pointer hover:bg-gray-100" @click="toggleSort('invoiceId')">
                Invoice ID
                <div class="ml-2">
                  <svg v-if="sortColumn === 'invoiceId'" class="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="sortDirection === 'asc'" fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                    <path v-else fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else class="h-4 w-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
            </th>

            <!-- Invoice -->
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div class="flex items-center cursor-pointer hover:bg-gray-100" @click="toggleSort('invoice')">
                Invoice
                <div class="ml-2">
                  <svg v-if="sortColumn === 'invoice'" class="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="sortDirection === 'asc'" fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                    <path v-else fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else class="h-4 w-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
            </th>

            <!-- WO # -->
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div class="flex items-center cursor-pointer hover:bg-gray-100" @click="toggleSort('woNumber')">
                WO #
                <div class="ml-2">
                  <svg v-if="sortColumn === 'woNumber'" class="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="sortDirection === 'asc'" fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                    <path v-else fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else class="h-4 w-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
            </th>

            <!-- Status -->
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div class="flex items-center cursor-pointer hover:bg-gray-100" @click="toggleSort('status')">
                Status
                <div class="ml-2">
                  <svg v-if="sortColumn === 'status'" class="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="sortDirection === 'asc'" fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                    <path v-else fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else class="h-4 w-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
            </th>

            <!-- Date -->
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div class="flex items-center cursor-pointer hover:bg-gray-100" @click="toggleSort('date')">
                Date
                <div class="ml-2">
                  <svg v-if="sortColumn === 'date'" class="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="sortDirection === 'asc'" fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                    <path v-else fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else class="h-4 w-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
            </th>

            <!-- Due Date -->
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div class="flex items-center cursor-pointer hover:bg-gray-100" @click="toggleSort('dueDate')">
                Due Date
                <div class="ml-2">
                  <svg v-if="sortColumn === 'dueDate'" class="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="sortDirection === 'asc'" fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                    <path v-else fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else class="h-4 w-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
            </th>

            <!-- Amount -->
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div class="flex items-center cursor-pointer hover:bg-gray-100" @click="toggleSort('amount')">
                Amount
                <div class="ml-2">
                  <svg v-if="sortColumn === 'amount'" class="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="sortDirection === 'asc'" fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                    <path v-else fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else class="h-4 w-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
            </th>

            <!-- Period -->
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div class="flex items-center cursor-pointer hover:bg-gray-100" @click="toggleSort('period')">
                Period
                <div class="ml-2">
                  <svg v-if="sortColumn === 'period'" class="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="sortDirection === 'asc'" fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                    <path v-else fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else class="h-4 w-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
            </th>

            <!-- Category -->
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div class="flex items-center cursor-pointer hover:bg-gray-100" @click="toggleSort('category')">
                Category
                <div class="ml-2">
                  <svg v-if="sortColumn === 'category'" class="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="sortDirection === 'asc'" fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                    <path v-else fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else class="h-4 w-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
            </th>

            <!-- Actions -->
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>

        <tbody class="bg-white divide-y divide-gray-200" v-if="paginatedData.length > 0">
          <tr 
            v-for="invoice in paginatedData" 
            :key="invoice.invoice"
            class="hover:bg-gray-50 cursor-pointer"
            @click="openAttachmentModal(invoice)"
          >
            <!-- Invoice ID -->
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
              {{ invoice.invoiceId }}
            </td>

            <!-- Invoice -->
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {{ invoice.invoice }}
            </td>

            <!-- WO # -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ invoice.woNumber }}
            </td>

            <!-- Status -->
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium" :class="statusClasses(invoice.status)">
              {{ invoice.status }}
            </td>

            <!-- Date -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ formatDate(invoice.date) }}
            </td>

            <!-- Due Date -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ formatDate(invoice.dueDate) }}
            </td>

            <!-- Amount -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              ${{ formatCurrency(invoice.amount) }}
            </td>

            <!-- Period -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ invoice.period }}
            </td>

            <!-- Category -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ invoice.category }}
            </td>

            <!-- Actions -->
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium" @click.stop>
              <button
                @click="openAttachmentModal(invoice)"
                class="text-[#00225E] hover:text-[#00225E]/80 font-medium"
              >
                View Attachments
              </button>
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr>
            <td colspan="10" class="px-6 py-4 text-center text-sm text-gray-500">
              No submitted invoices found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Summary Row -->
    <div v-if="paginatedData.length > 0" class="bg-gray-50 border-t border-gray-200 px-6 py-4">
      <div class="flex justify-between items-center">
        <div class="text-sm text-gray-600">
          <span class="font-medium">Total Records:</span> {{ filteredData.length }}
        </div>
        <div class="text-sm text-gray-600">
          <span class="font-medium">Total Amount:</span> 
          <span class="font-bold text-lg text-gray-900 ml-2">
            ${{ formatCurrency(totalAmount) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
      <div class="flex flex-1 justify-between sm:hidden">
        <button
          @click="previousPage"
          :disabled="currentPage === 1"
          class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Previous
        </button>
        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Next
        </button>
      </div>
      <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            Showing
            <span class="font-medium">{{ (currentPage - 1) * itemsPerPage + 1 }}</span>
            to
            <span class="font-medium">{{ Math.min(currentPage * itemsPerPage, filteredData?.length) }}</span>
            of
            <span class="font-medium">{{ filteredData?.length }}</span>
            results
          </p>
        </div>
        <div>
          <nav class="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              @click="previousPage"
              :disabled="currentPage === 1"
              class="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
            >
              <span class="sr-only">Previous</span>
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clip-rule="evenodd" />
              </svg>
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="goToPage(page)"
              :class="[
                page === currentPage
                  ? 'relative z-10 inline-flex items-center bg-[#00225E] px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#00225E]'
                  : 'relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
            >
              <span class="sr-only">Next</span>
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clip-rule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>

    <!-- Attachment Modal -->
    <AttachmentModal 
      :isOpen="showAttachmentModal"
      :rowData="selectedRow"
      @close="closeAttachmentModal"
      @refresh="handleRefreshData"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import AdvancedFilters from "./AdvancedFilters.vue";
import AttachmentModal from "./AttachmentModal.vue";

export default {
  components: {
    AdvancedFilters,
    AttachmentModal
  },
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const store = useStore();
    const searchQuery = ref("");
    const sortColumn = ref("invoice");
    const sortDirection = ref("asc");
    const currentPage = ref(1);
    const itemsPerPage = 10;
    const activeFilters = ref({});
    const showAttachmentModal = ref(false);
    const selectedRow = ref({});

    // Use data from props
    const invoices = computed(() => props.data || []);
    
    // Get user companies from store
    const userCompanies = computed(() => store.getters['userCompanies'] || []);

    // Define export columns
    const exportColumns = [
      { key: 'invoiceId', label: 'Invoice ID', type: 'text' },
      { key: 'invoice', label: 'Invoice', type: 'text' },
      { key: 'amount', label: 'Amount', type: 'currency' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'period', label: 'Period', type: 'text' },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'woNumber', label: 'WO #', type: 'text' },
      { key: 'dueDate', label: 'Due Date', type: 'date' },
      { key: 'status', label: 'Status', type: 'text' }
    ];

    const filteredData = computed(() => {
      let filtered = invoices.value || [];

      // Apply search query
      if (searchQuery.value) {
        filtered = filtered.filter(invoice =>
          (invoice.invoiceId && invoice.invoiceId.toString().toLowerCase().includes(searchQuery.value.toLowerCase())) ||
          (invoice.invoice && invoice.invoice.toString().toLowerCase().includes(searchQuery.value.toLowerCase())) ||
          (invoice.category && invoice.category.toString().toLowerCase().includes(searchQuery.value.toLowerCase())) ||
          (invoice.status && invoice.status.toString().toLowerCase().includes(searchQuery.value.toLowerCase())) ||
          (invoice.woNumber && invoice.woNumber.toString().toLowerCase().includes(searchQuery.value.toLowerCase()))
        );
      }

      // Apply advanced filters
      if (activeFilters.value.category) {
        filtered = filtered.filter(invoice => invoice.category === activeFilters.value.category);
      }

      if (activeFilters.value.status) {
        // Use "contains" logic to match status variations (e.g., "Submitted" matches "Submitted - past due")
        filtered = filtered.filter(invoice => 
          invoice.status && invoice.status.toLowerCase().includes(activeFilters.value.status.toLowerCase())
        );
      }

      if (activeFilters.value.woNumber) {
        filtered = filtered.filter(invoice => invoice.woNumber === activeFilters.value.woNumber);
      }

      if (activeFilters.value.datePeriod) {
        filtered = filtered.filter(invoice => invoice.period === activeFilters.value.datePeriod);
      }

      if (activeFilters.value.fromDate || activeFilters.value.toDate) {
        filtered = filtered.filter(invoice => {
          const invoiceDate = new Date(invoice.date);
          const fromDate = activeFilters.value.fromDate ? new Date(activeFilters.value.fromDate) : null;
          const toDate = activeFilters.value.toDate ? new Date(activeFilters.value.toDate) : null;
          
          if (fromDate && invoiceDate <= fromDate) return false;
          if (toDate && invoiceDate >= toDate) return false;
          return true;
        });
      }

      return filtered;
    });

    const sortedData = computed(() => {
      // Create a copy with index to ensure stable sorting
      const dataWithIndex = filteredData.value.map((item, index) => ({ ...item, _index: index }));
      
      return dataWithIndex.sort((a, b) => {
        let aVal = a[sortColumn.value];
        let bVal = b[sortColumn.value];
        
        // Handle null/undefined values
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';
        
        // Check if values are numbers
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        const bothNumbers = !isNaN(aNum) && !isNaN(bNum);
        
        let comparison = 0;
        
        if (bothNumbers) {
          // Numeric comparison
          comparison = aNum - bNum;
        } else {
          // String comparison
          const aStr = aVal.toString().toLowerCase();
          const bStr = bVal.toString().toLowerCase();
          
          if (aStr < bStr) comparison = -1;
          else if (aStr > bStr) comparison = 1;
          else comparison = 0;
        }
        
        // If values are equal, maintain original order using index
        if (comparison === 0) {
          comparison = a._index - b._index;
        }
        
        return sortDirection.value === "asc" ? comparison : -comparison;
      });
    });

    const totalPages = computed(() => Math.ceil(sortedData.value.length / itemsPerPage));

    const paginatedData = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      return sortedData.value.slice(start, end);
    });

    const totalAmount = computed(() => {
      return filteredData.value.reduce((sum, invoice) => {
        return sum + (parseFloat(invoice.amount) || 0);
      }, 0);
    });

    const visiblePages = computed(() => {
      const pages = [];
      const start = Math.max(1, currentPage.value - 2);
      const end = Math.min(totalPages.value, start + 4);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    });

    const toggleSort = (column) => {
      if (sortColumn.value === column) {
        sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
      } else {
        sortColumn.value = column;
        sortDirection.value = "asc";
      }
    };

    const statusClasses = (status) => {
      if (!status) return "text-gray-600 bg-gray-100";
      
      const statusLower = status.toLowerCase();
      
      // Use "contains" logic to match status variations
      if (statusLower.includes('verified')) return "text-blue-600 bg-blue-100";
      if (statusLower.includes('reject') || statusLower.includes('dispute')) return "text-blue-600 bg-blue-100";
      if (statusLower.includes('approved')) return "text-green-600 bg-green-100";
      if (statusLower.includes('queue')) return "text-yellow-600 bg-yellow-100";
      if (statusLower.includes('paid')) return "text-green-600 bg-green-100";
      
      return "text-gray-600 bg-gray-100";
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      // Parse date as local time to avoid timezone issues
      const [year, month, day] = dateString.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const previousPage = () => {
      if (currentPage.value > 1) currentPage.value--;
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) currentPage.value++;
    };

    const goToPage = (page) => {
      currentPage.value = page;
    };

    const updateSearchQuery = (query) => {
      searchQuery.value = query;
    };

    const handleFilterChange = async (filters) => {
      activeFilters.value = filters;
      
      
      // Make API call with filters
      try {
        const response = await store.dispatch('getSubmittedInvoices', filters);
        if (response.ok) {
          // Update the data with filtered results
          invoices.value = response.data;
          
        } else {
          console.error('Error applying filters:', response.error);
        }
      } catch (error) {
        console.error('Error applying filters:', error);
      }
    };

    const handleExportData = async (filters) => {
      
      
      try {
        const response = await store.dispatch('getSubmittedInvoices', filters);
        if (response.ok) {
          
          return response.data;
        } else {
          console.error('Error fetching data for export:', response.error);
          return [];
        }
      } catch (error) {
        console.error('Error fetching data for export:', error);
        return [];
      }
    };

    const openAttachmentModal = (row) => {
      selectedRow.value = row;
      showAttachmentModal.value = true;
    };

    const closeAttachmentModal = () => {
      showAttachmentModal.value = false;
      selectedRow.value = {};
    };

    const handleRefreshData = () => {
      console.log('Attachment uploaded, requesting parent to refresh data...');
      // Emit event to parent (InvoiceTabs) to reload data
      emit('refresh');
    };

    onMounted(() => {
      
    });

    return {
      searchQuery,
      sortColumn,
      sortDirection,
      currentPage,
      itemsPerPage,
      totalPages,
      paginatedData,
      visiblePages,
      toggleSort,
      statusClasses,
      formatCurrency,
      formatDate,
      previousPage,
      nextPage,
      goToPage,
      updateSearchQuery,
      handleFilterChange,
      handleExportData,
      exportColumns,
      showAttachmentModal,
      selectedRow,
      openAttachmentModal,
      closeAttachmentModal,
      handleRefreshData,
      userCompanies,
      filteredData,
      totalAmount
    };
  }
};
</script>
